import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";

const inputCls = "w-full bg-transparent border-b border-white/15 focus:border-champagne outline-none py-2.5 text-sm font-light text-ivory placeholder:text-stone-600 transition-colors duration-300";
const labelCls = "text-[9px] font-mono tracking-[0.25em] uppercase text-stone-500";

export default function GalleryCreateForm({ onCancel, onCreated }) {
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ title: "", couple: "", passcode: "", welcome: "", enquiry_id: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    adminApi.get("/enquiries").then((r) => setBooked(r.data.filter((e) => e.status === "booked"))).catch(() => {});
  }, []);

  const pickEnquiry = (e) => {
    const id = e.target.value;
    const enq = booked.find((b) => b.id === id);
    setForm({
      ...form,
      enquiry_id: id,
      couple: enq ? enq.full_names : form.couple,
      title: enq ? `${enq.full_names} — ${enq.location}` : form.title,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.passcode.length < 4) return toast.error("Passcode needs at least 4 characters");
    setSaving(true);
    try {
      const { data } = await adminApi.post("/galleries", { ...form, enquiry_id: form.enquiry_id || null });
      toast.success("Gallery created — now add their photos");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="gallery-create-form" className="max-w-xl space-y-6">
      <button type="button" data-testid="gallery-create-back" onClick={onCancel} className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.2em] uppercase text-stone-400 hover:text-champagne transition-colors"><ArrowLeft size={12} /> Back</button>
      <h2 className="font-serif text-2xl text-ivory">New <span className="italic text-champagne">Client Gallery</span></h2>
      {booked.length > 0 && (
        <div>
          <label className={labelCls} htmlFor="gallery-enquiry">Link to a booked couple (optional)</label>
          <select id="gallery-enquiry" data-testid="gallery-enquiry-select" value={form.enquiry_id} onChange={pickEnquiry} className={inputCls}>
            <option value="" className="bg-surface">— none —</option>
            {booked.map((b) => <option key={b.id} value={b.id} className="bg-surface">{b.full_names} · {b.event_date}</option>)}
          </select>
        </div>
      )}
      <div><label className={labelCls} htmlFor="gallery-title">Gallery title *</label><input id="gallery-title" data-testid="gallery-title-input" required value={form.title} onChange={set("title")} className={inputCls} placeholder="Ava & Noah — Villa Balbiano" /></div>
      <div><label className={labelCls} htmlFor="gallery-couple">Couple</label><input id="gallery-couple" data-testid="gallery-couple-input" value={form.couple} onChange={set("couple")} className={inputCls} placeholder="Ava & Noah Bennett" /></div>
      <div><label className={labelCls} htmlFor="gallery-passcode">Passcode * (share it privately with the couple)</label><input id="gallery-passcode" data-testid="gallery-passcode-input" required minLength={4} value={form.passcode} onChange={set("passcode")} className={inputCls} placeholder="e.g. como2026" /></div>
      <div><label className={labelCls} htmlFor="gallery-welcome">Welcome note</label><textarea id="gallery-welcome" data-testid="gallery-welcome-input" rows={3} value={form.welcome} onChange={set("welcome")} className={`${inputCls} resize-none`} placeholder="Dear Ava & Noah, here is your day…" /></div>
      <div className="flex items-center gap-3 pt-2">
        <button type="submit" data-testid="gallery-create-submit" disabled={saving} className="bg-champagne text-obsidian px-8 py-3.5 text-[11px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors disabled:opacity-50">{saving ? "Creating…" : "Create Gallery"}</button>
        <button type="button" onClick={onCancel} className="border border-white/15 px-6 py-3.5 text-[11px] font-mono tracking-[0.2em] uppercase text-stone-300 hover:border-champagne hover:text-champagne transition-colors">Cancel</button>
      </div>
    </form>
  );
}
